import { memo, useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import LocalPostOfficeIcon from '@mui/icons-material/LocalPostOffice';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLanguage } from '../../../../context/LanguageContext';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';

export type ChannelType = 'email' | 'sms' | 'cashdesk' | 'wallet' | 'courrier' | '';

export interface DefinitionFormData {
  channel: ChannelType;
  title: string;
  description: string;
  sendDate: string;
  sendByCustomer: boolean;
  hasOffer: boolean;
  followUpDate: string;
}

interface CommunicationDefinitionStepProps {
  data: DefinitionFormData;
  onChange: (patch: Partial<DefinitionFormData>) => void;
}

const CHANNELS = [
  { value: 'courrier', label: 'Courrier', Icon: LocalPostOfficeIcon      },
  { value: 'email',    label: 'Email',    Icon: EmailIcon                },
  { value: 'sms',      label: 'SMS',      Icon: SmsIcon                  },
  { value: 'wallet',   label: 'Wallet',   Icon: AccountBalanceWalletIcon },
  { value: 'cashdesk', label: 'Caisse',   Icon: PointOfSaleIcon          },
];

function CommunicationDefinitionStep({ data, onChange }: CommunicationDefinitionStepProps) {
  const { t } = useLanguage();
  const { sidebarColor } = useLayoutPreferences();

  const [title, setTitle] = useState(data.title);
  const [description, setDescription] = useState(data.description);
  const [sendDate, setSendDate] = useState(data.sendDate);
  const [followUpDate, setFollowUpDate] = useState(data.followUpDate);

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        {t('pages.marketing.communication.definition')}
      </Typography>

      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        {t('pages.marketing.communication.canal')}
      </Typography>

      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 7 }}>
          <ToggleButtonGroup
            value={data.channel}
            exclusive
            onChange={(_, val) => {
              if (val) onChange({ channel: val as ChannelType });
            }}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
          >
            {CHANNELS.map(({ value, label, Icon }) => (
              <ToggleButton
                key={value}
                value={value}
                sx={{
                  flexDirection: 'column',
                  gap: 0.5,
                  width: 110,
                  py: 1.2,
                  border: '1px solid #dee2e6 !important',
                  borderRadius: '8px !important',
                  color: '#495057',
                  '&.Mui-selected': {
                    backgroundColor: `${sidebarColor}15`,
                    color: sidebarColor,
                    borderColor: `${sidebarColor} !important`,
                    fontWeight: 600,
                  },
                  '&:hover': {
                    backgroundColor: `${sidebarColor}10`,
                  },
                }}
              >
                <Icon fontSize="small" />
                <Typography variant="caption" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                  {label}
                </Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Alert
            icon={<InfoOutlinedIcon fontSize="small" />}
            severity="info"
            sx={{ fontSize: '0.8rem', borderRadius: 2 }}
          >
            <Typography variant="caption" fontWeight={600} display="block">
              Rappel: Définition, programmation, template 2
            </Typography>
            <Typography variant="caption">
              Votre communication par <strong>email</strong> est entièrement paramétrable. Mais rassurez-vous des modèles sont à votre disposition.
            </Typography>
          </Alert>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            fullWidth
            required
            label={t('pages.marketing.communication.title')}
            value={title}
            onChange={e => setTitle(e.target.value)}
            onBlur={e => onChange({ title: e.target.value })}
            size="small"
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('pages.marketing.communication.description')}
            value={description}
            onChange={e => setDescription(e.target.value)}
            onBlur={e => onChange({ description: e.target.value })}
            size="small"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
        {t('pages.marketing.communication.programmation')}
      </Typography>
      <TextField
        fullWidth
        label={t('pages.marketing.communication.sendDate')}
        type="datetime-local"
        value={sendDate}
        onChange={e => setSendDate(e.target.value)}
        onBlur={e => onChange({ sendDate: e.target.value })}
        size="small"
        InputLabelProps={{ shrink: true }}
        sx={{ maxWidth: 360 }}
      />

      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={data.sendByCustomer}
              onChange={e => onChange({ sendByCustomer: e.target.checked })}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: sidebarColor },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: sidebarColor },
              }}
            />
          }
          label={
            <Typography variant="body2">
              {t('pages.marketing.communication.sendByCustomer')}
            </Typography>
          }
        />
      </Box>

      <Box sx={{ mt: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={!data.hasOffer}
              onChange={e => onChange({ hasOffer: !e.target.checked })}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: sidebarColor },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: sidebarColor },
              }}
            />
          }
          label={
            <Typography variant="body2">
              {t('pages.marketing.communication.noOffer')}
            </Typography>
          }
        />
      </Box>

      {!data.hasOffer && (
        <TextField
          fullWidth
          label={t('pages.marketing.communication.followUpDate')}
          type="date"
          value={followUpDate}
          onChange={e => setFollowUpDate(e.target.value)}
          onBlur={e => onChange({ followUpDate: e.target.value })}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 2, maxWidth: 360 }}
        />
      )}
    </Box>
  );
}

export default memo(CommunicationDefinitionStep);
